import { useEffect, useReducer } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { AIPlugin } from '../../../../aiPluginType';
import { aiSessionContextPluginKey } from '../../../../pm-plugins/ai-session-context/ai-session-context-plugin-key';

export type State =
	| 'discard'
	| 'loading'
	| 'idle'
	| 'error'
	| 'prompt'
	| 'acceptReject'
	| 'noImprovement';

export type Action =
	| { type: 'discard' }
	| { type: 'cancel' }
	| { type: 'reset' }
	| { type: 'terminate' }
	| { type: 'show-accept-reject' };

interface UIState {
	discard: boolean;
	showAcceptReject: boolean;
	terminated: boolean;
}

const initialState: UIState = {
	discard: false,
	terminated: false,
	showAcceptReject: false,
};

function reducer(state: UIState, action: Action): UIState {
	switch (action.type) {
		case 'discard':
			return { ...state, discard: true };
		case 'cancel':
			return { ...state, discard: false };
		case 'terminate':
			return { ...state, terminated: true };
		case 'reset':
			return { discard: false, terminated: false, showAcceptReject: false };
		case 'show-accept-reject':
			return { ...state, showAcceptReject: true };
		default:
			return state;
	}
}

/**
 * LEGACY MODE (xstate) - when feature flag is OFF
 */
const useUIStateLegacy = (api?: ExtractInjectionAPI<AIPlugin>): [State, React.Dispatch<Action>] => {
	const [localUI, dispatch] = useReducer(reducer, initialState);

	const { nextSessionId } = useSharedPluginStateWithSelector(api, ['aiExperience'], (state) => ({
		nextSessionId: state.aiExperienceState?.aiDecorationExperiencePluginState?.nextSessionId,
	}));

	useEffect(() => {
		if (!nextSessionId) {
			dispatch({ type: 'reset' });
		}
	}, [nextSessionId]);

	if (nextSessionId && !localUI.terminated) {
		if (localUI.discard) {
			return ['discard', dispatch];
		}
		return ['loading', dispatch];
	}
	return ['idle', () => {}];
};

/**
 * MIGRATED MODE (ProseMirror native) - when feature flag is ON
 * Uses ai-session-context plugin status as source of truth.
 */
const useUIStateMigrated = (
	api?: ExtractInjectionAPI<AIPlugin>,
): [State, React.Dispatch<Action>] => {
	const [localUI, dispatch] = useReducer(reducer, initialState);

	const { nextSessionId, status, responseTooSimilar, streamsMarkdown } =
		useSharedPluginStateWithSelector(api, ['aiExperience'], (state) => ({
			nextSessionId: state.aiExperienceState?.aiDecorationExperiencePluginState?.nextSessionId,
			status: state.aiExperienceState?.aiSessionPluginState?.status,
			responseTooSimilar: state.aiExperienceState?.aiSessionPluginState?.responseTooSimilar,
			streamsMarkdown:
				state.aiExperienceState?.aiDecorationExperiencePluginState?.configItem?.streamsMarkdown,
		}));

	const isMarkdownStreaming = !!streamsMarkdown;

	// For markdown streaming flows, auto-end the experience when streaming completes.
	// Markdown streaming applies changes outside the ProseMirror document (e.g. updating the
	// Confluence page title via eventHub events), so there's no accept/reject step — the
	// experience should end immediately on completion.
	useEffect(() => {
		if (isMarkdownStreaming && status === 'complete') {
			api?.aiExperience?.actions.endAIExperience();
		}
	}, [isMarkdownStreaming, status, api]);

	// After terminating (e.g. discard confirmed), keep UI suppressed until the session fully ends.
	// Otherwise we can briefly show "loading" while `nextSessionId` is still present.
	// Also reset the session context when terminating from an error state — endSession may
	// skip the RESET dispatch when nextSessionId is empty (sessionId mismatch guard).
	useEffect(() => {
		if (localUI.terminated && !nextSessionId) {
			if (status === 'error') {
				api?.core?.actions.execute(({ tr }) => {
					tr.setMeta(aiSessionContextPluginKey, { type: 'RESET' });
					return tr;
				});
			}
			dispatch({ type: 'reset' });
		}
	}, [localUI.terminated, nextSessionId, status, api]);

	const resolveState = (): State => {
		if (localUI.terminated) {
			return 'idle';
		}

		if (localUI.discard) {
			return 'discard';
		}

		if (status === 'complete' && responseTooSimilar) {
			return 'noImprovement';
		}

		// Active session that's not in error/idle = show loading
		if (nextSessionId && status !== 'error' && status !== 'idle') {
			return 'loading';
		}

		// Error takes priority over acceptReject — if the session errored,
		// show the error screen even if acceptReject was previously requested.
		if (status === 'error') {
			return 'error';
		}

		if (localUI.showAcceptReject && !nextSessionId) {
			return 'acceptReject';
		}

		// Map plugin status to UI state
		switch (status) {
			case 'prompt':
				return 'prompt';
			case 'streaming':
			case 'complete':
				return 'loading';
			case 'idle':
			default:
				return 'idle';
		}
	};

	return [resolveState(), dispatch];
};

export const useUIState = conditionalHooksFactory(
	() => expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true),
	useUIStateMigrated,
	useUIStateLegacy,
);
